<!-- Template -->
<template>
  <div class="container">
    <div class="input-container">
      <input
        :value="value"
        placeholder="yyyy-mm-dd"
        :class="[styling]"
        @focusin="onFocusIn"
        @focusout="onFocusOut"
        @input="(event: any) => $emit('inputValueChanged', event.target.value)"
      />
    </div>
    <div class="message-container">
      <label v-if="error">{{ error }}</label>
    </div>
  </div>
</template>
<!-- Script -->
<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { format } from 'date-fns'
import { computed, ref } from 'vue'
import { LOCALES } from '@/utils/locales'

const { locale } = useI18n()
const props = defineProps({
  inputValue: {
    required: false,
    type: String
  },

  formattedValue: {
    required: false,
    type: String
  },

  error: {
    required: false,
    type: String
  }
})

const isFocused = ref(false)

function onFocusIn() {
  isFocused.value = true
}
function onFocusOut() {
  isFocused.value = false
}

const value = computed(() => {
  if (isFocused.value) {
    return props.inputValue
  } else {
    return props.inputValue ? formatDateString(props.inputValue) : props.inputValue
  }
})

defineEmits<{
  (event: 'inputValueChanged', value: string): void
}>()
function formatDateString(value: string) {
  const date = new Date(value)
  const formatting = 'dd MMM yyyy'
  const options = { locale: LOCALES[locale.value] }

  const capitalized = format(date, formatting, options).charAt(0).toUpperCase()
  const lowercased = format(date, formatting, options).slice(1)
  return capitalized + lowercased
}

const styling = computed<string>(() => {
  return props.error ? 'error' : ''
})
</script>
<!-- Style -->
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';

.container {
  display: flex;
  flex-direction: column;
  gap: variables.$gap-small;
  .input-container {
    input {
      @include font-mixins.desktop-bold-paragraph;
      border-style: solid;
      border-radius: variables.$border-radius-medium;
      border-width: variables.$border-width-xsmall;
      border-color: variables.$color-greyscale-secondary;
      padding: variables.$padding-large variables.$padding-xlarge;
      max-width: 140px;
      &.error {
        border-color: variables.$color-error;
        &:focus-within {
          border-width: variables.$border-width-xsmall;
          border-color: variables.$color-error;
          outline-style: solid;
          outline-width: variables.$border-width-medium;
          outline-color: variables.$color-error-outline;
        }
      }
      &:focus-within {
        border-width: variables.$border-width-xsmall;
        border-color: variables.$color-primary;
        outline-style: solid;
        outline-width: variables.$border-width-medium;
        outline-color: variables.$color-primary-light;
      }
    }
  }
  .message-container {
    height: 5px;
    label {
      @include font-mixins.desktop-bold-label;
      color: variables.$color-error;
    }
  }
}
</style>
